import { format, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { parseISO } from 'date-fns';

// Часовой пояс Молдовы
export const MOLDOVA_TIMEZONE = 'Europe/Chisinau';

/**
 * Конвертирует дату из UTC в часовой пояс Молдовы
 * @param date - Дата в UTC (строка или Date объект)
 * @returns Date объект в часовом поясе Молдовы
 */
export function convertToMoldovaTime(date: string | Date): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return toZonedTime(dateObj, MOLDOVA_TIMEZONE);
}

/**
 * Конвертирует дату из часового пояса Молдовы в UTC
 * @param date - Дата в часовом поясе Молдовы (строка или Date объект)
 * @returns Date объект в UTC
 */
export function convertFromMoldovaTime(date: string | Date): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return fromZonedTime(dateObj, MOLDOVA_TIMEZONE);
}

/**
 * Форматирует дату в строку с учётом часового пояса Молдовы
 * @param date - Дата в UTC
 * @param formatString - Формат строки (по умолчанию 'dd.MM.yyyy HH:mm')
 * @returns Отформатированная строка
 */
export function formatMoldovaTime(date: string | Date, formatString: string = 'dd.MM.yyyy HH:mm'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const zonedDate = toZonedTime(dateObj, MOLDOVA_TIMEZONE);
  return format(zonedDate, formatString, { timeZone: MOLDOVA_TIMEZONE });
}

/**
 * Конвертирует значение из datetime-local input в UTC ISO string
 * @param datetimeLocal - Значение из input type="datetime-local" (формат: "2026-01-20T07:00")
 * @returns ISO string в UTC
 */
export function datetimeLocalToUTC(datetimeLocal: string): string {
  if (!datetimeLocal) return '';

  // datetime-local возвращает строку без часового пояса.
  // Интерпретируем ее как время Europe/Chisinau, независимо от TZ устройства.
  const normalized = datetimeLocal.trim().replace('T', ' ');
  const withSeconds = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}$/.test(normalized)
    ? `${normalized}:00`
    : normalized;
  const utcDate = fromZonedTime(withSeconds, MOLDOVA_TIMEZONE);
  return Number.isNaN(utcDate.getTime()) ? '' : utcDate.toISOString();
}

/**
 * Конвертирует UTC ISO string в формат для datetime-local input
 * @param utcIsoString - ISO string в UTC
 * @returns Строка в формате "YYYY-MM-DDTHH:mm" для datetime-local input
 */
export function utcToDatetimeLocal(utcIsoString: string): string {
  if (!utcIsoString) return '';
  
  const moldovaDate = convertToMoldovaTime(utcIsoString);
  // Форматируем в формат datetime-local: "YYYY-MM-DDTHH:mm"
  const year = moldovaDate.getFullYear();
  const month = String(moldovaDate.getMonth() + 1).padStart(2, '0');
  const day = String(moldovaDate.getDate()).padStart(2, '0');
  const hours = String(moldovaDate.getHours()).padStart(2, '0');
  const minutes = String(moldovaDate.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}
